interface JsonDisplayProps {
  data: unknown
}

interface NestedFieldProps {
  label: string
  value: unknown
}

const NestedField = ({ label, value }: NestedFieldProps) => {
  const isObject = value !== null && typeof value === 'object'
  const isArray = Array.isArray(value)
  const hasContent =
    isObject &&
    (isArray
      ? (value as unknown[]).length > 0
      : Object.keys(value as Record<string, unknown>).length > 0)

  if (isObject && hasContent) {
    const entries = isArray
      ? (value as unknown[]).map((item, idx) => ({
          key: `[${idx}]`,
          value: item,
        }))
      : Object.entries(value as Record<string, unknown>).map(([key, val]) => ({
          key,
          value: val,
        }))

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-p-500 text-secondary">{label}:</span>
          <span className="text-xs-400">{isArray ? `[${(value as unknown[]).length}]` : ``}</span>
        </div>
        <div className="relative ml-4 pl-4">
          {/* Vertical line connecting all children */}
          <div className="absolute left-0 top-0 bottom-2 w-px bg-st1" />

          <div className="space-y-2">
            {entries.map((entry, idx) => (
              <div key={`${label}-${entry.key}-${idx}`} className="relative">
                {/* Horizontal line connecting to vertical */}
                <div className="absolute -left-4 top-1/2 h-px w-3 bg-st1" />
                <NestedField label={entry.key} value={entry.value} />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  let displayValue: string
  if (value === null) {
    displayValue = 'null'
  } else if (value === undefined) {
    displayValue = 'undefined'
  } else {
    displayValue = JSON.stringify(value)
  }

  return (
    <div className="flex items-start gap-3 text-p-400 text-secondary">
      <span className="text-p-500 min-w-fit">{label}:</span>
      <span className="break-words text-primary">{displayValue}</span>
    </div>
  )
}

export const JsonDisplay = ({ data }: JsonDisplayProps) => {
  if (data === null || data === undefined) return <span className="text-tertiary">N/A</span>

  try {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data

    if (typeof parsed !== 'object' || parsed === null) {
      return <span className="text-p-400 text-secondary">{String(parsed)}</span>
    }

    const entries = Object.entries(parsed as Record<string, unknown>)
    return (
      <div className="space-y-2">
        {entries.map(([key, value]) => (
          <NestedField key={key} label={key} value={value} />
        ))}
      </div>
    )
  } catch {
    return <span className="text-p-400 text-secondary break-words">{String(data)}</span>
  }
}
